import { useEffect } from "react";
import { toast } from "react-toastify";
import {
  getCompanyDocumentDownloadEventsUrl,
  getCompanyDocumentDownloadNotifications,
} from "../../api/companyDocuments";
import { useAuthStore } from "../../store";
import { useVaultDownloadNotificationStore } from "../../store/useVaultDownloadNotificationStore";
import { normalizeVaultDownloadNotification } from "../../types/companyDocuments";

const DOWNLOAD_EVENT_NAMES = [
  "DOCUMENT_VAULT_DOWNLOAD",
  "DOWNLOAD_READY",
  "DOWNLOAD_FAILED",
];

function parseEventNotification(value: string) {
  try {
    return normalizeVaultDownloadNotification(JSON.parse(value));
  } catch (_error) {
    return null;
  }
}

/**
 * Keeps the top-bar notification menu in sync while the user navigates around
 * CapTable. EventSource cannot pass the application's custom Axios headers,
 * so the backend accepts the existing AccessToken query-token convention.
 */
export default function VaultDownloadEventListener() {
  const companyId = useAuthStore((state) => state.companyId || "");
  const accessToken = useAuthStore(
    (state) => state.accessToken || sessionStorage.getItem("accesstoken") || ""
  );
  const setNotifications = useVaultDownloadNotificationStore(
    (state) => state.setNotifications
  );
  const upsertNotification = useVaultDownloadNotificationStore(
    (state) => state.upsertNotification
  );
  const clear = useVaultDownloadNotificationStore((state) => state.clear);

  useEffect(() => {
    if (!companyId || !accessToken) {
      clear();
      return undefined;
    }

    // The notification menu lives outside the company route. Clear it before
    // loading the next company's durable history so a company switch can never
    // briefly expose another company's download notifications.
    clear();

    let disposed = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let retryCount = 0;

    const refreshNotifications = async () => {
      try {
        const notifications = await getCompanyDocumentDownloadNotifications();
        if (!disposed) setNotifications(companyId, notifications);
      } catch (_error) {
        // The stream can still deliver a completion if the initial history
        // lookup is temporarily unavailable, so avoid surfacing a noisy toast.
      }
    };

    const handleEvent = (event: MessageEvent) => {
      const notification = parseEventNotification(event.data);
      if (!notification || disposed) {
        refreshNotifications().catch(() => undefined);
        return;
      }
      upsertNotification(companyId, notification);
      if (notification.status === "READY") {
        toast("Your document download is ready and available for 24 hours.", {
          type: "success",
          autoClose: 4000,
        });
      } else if (notification.status === "FAILED") {
        toast(
          notification.message ||
            "Your document download could not be prepared.",
          {
            type: "error",
            autoClose: 4000,
          }
        );
      }
    };

    const connect = () => {
      if (disposed) return;
      eventSource = new EventSource(
        getCompanyDocumentDownloadEventsUrl({ accessToken, companyId })
      );
      DOWNLOAD_EVENT_NAMES.forEach((eventName) => {
        eventSource?.addEventListener(eventName, handleEvent);
      });
      eventSource.onmessage = handleEvent;
      eventSource.onopen = () => {
        retryCount = 0;
        // Events may have been emitted while the browser was reconnecting.
        // Rehydrate from the durable notification endpoint to cover that gap.
        refreshNotifications().catch(() => undefined);
      };
      eventSource.onerror = () => {
        eventSource?.close();
        if (disposed) return;
        refreshNotifications().catch(() => undefined);
        const delay = Math.min(2000 * 2 ** retryCount, 30000);
        retryCount += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    refreshNotifications().catch(() => undefined);
    connect();

    return () => {
      disposed = true;
      eventSource?.close();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [accessToken, clear, companyId, setNotifications, upsertNotification]);

  return null;
}
